import {
  type AdminOrgDetail,
  captionMinutesUsed,
  effectiveRole,
  formatBytes,
  formatPlanPrice,
  ORG_ROLES,
  type OrgRole,
  PLAN_IDS,
  PLANS,
  type PlanId,
  ROLE_LABELS,
  STORAGE_CATEGORY_LABELS,
} from "@cva/shared";
import { keepPreviousData, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useParams } from "@tanstack/react-router";
import { ArrowLeft, Search, UserMinus, UserPlus } from "lucide-react";
import { type FormEvent, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Badge, Card, CardHeader, Progress, Skeleton } from "../../components/ui/card";
import { ConfirmDialog } from "../../components/ui/confirm";
import { Field, Input, Select } from "../../components/ui/input";
import { client, orpc } from "../../lib/api";
import { errorMessage, formatDate, formatDateTime } from "../../lib/utils";
import { AdminPageHeader } from "./layout";
import { Pager } from "./users";

const PAGE = 50;
const usedPercent = (used: number, quota: number) => (quota > 0 ? (used / quota) * 100 : 0);
const tone = (percent: number) => (percent >= 95 ? "danger" : percent >= 80 ? "warning" : "brand");

export function AdminOrgsPage() {
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const orgs = useQuery({
    ...orpc.admin.orgs.list.queryOptions({ input: { query, limit: PAGE, offset } }),
    placeholderData: keepPreviousData,
  });
  const total = orgs.data?.total ?? 0;
  return (
    <>
      <AdminPageHeader title="Organisationen" description={`${total} Organisationen`} />
      <div className="relative mb-4 max-w-md">
        <Search className="absolute top-2.5 left-3 size-4 text-zinc-400" />
        <Input
          className="pl-9"
          placeholder="Name oder Kürzel"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setOffset(0);
          }}
        />
      </div>
      <Card>
        {orgs.isLoading ? (
          <Skeleton className="m-5 h-40" />
        ) : (
          <table className="w-full text-sm">
            <thead className="border-b border-zinc-100 text-left text-xs text-zinc-500">
              <tr>
                <th className="px-5 py-2.5 font-medium">Organisation</th>
                <th className="px-3 py-2.5 font-medium">Tarif</th>
                <th className="px-3 py-2.5 text-right font-medium">Mitglieder</th>
                <th className="px-3 py-2.5 text-right font-medium">Projekte</th>
                <th className="px-3 py-2.5 text-right font-medium">Dateien</th>
                <th className="px-3 py-2.5 text-right font-medium">Medien</th>
                <th className="w-56 px-3 py-2.5 font-medium">Speicher</th>
                <th className="px-5 py-2.5 font-medium">Angelegt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 tabular-nums">
              {orgs.data?.items.map((org) => {
                const percent = usedPercent(org.storageBytes, org.quotaBytes);
                return (
                  <tr key={org.id} className="relative cursor-pointer hover:bg-zinc-50">
                    <td className="px-5 py-2.5">
                      <Link
                        to="/admin/orgs/$orgId"
                        params={{ orgId: org.id }}
                        className="font-medium text-zinc-900 after:absolute after:inset-0 hover:text-brand-700"
                      >
                        {org.name}
                      </Link>
                      <span className="block text-xs text-zinc-500">{org.slug}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      <Badge tone={org.plan === "intern" ? "neutral" : "brand"}>
                        {PLANS[org.plan].name}
                      </Badge>
                    </td>
                    <td className="px-3 py-2.5 text-right">{org.memberCount}</td>
                    <td className="px-3 py-2.5 text-right">{org.projectCount}</td>
                    <td className="px-3 py-2.5 text-right">{org.documentCount}</td>
                    <td className="px-3 py-2.5 text-right">{org.assetCount}</td>
                    <td className="px-3 py-2.5">
                      <Progress value={percent} tone={tone(percent)} />
                      <span className="text-xs text-zinc-500">
                        {formatBytes(org.storageBytes)} / {formatBytes(org.quotaBytes)}
                      </span>
                    </td>
                    <td className="px-5 py-2.5 text-zinc-600">{formatDate(org.createdAt)}</td>
                  </tr>
                );
              })}
              {orgs.data?.items.length === 0 ? (
                <tr>
                  <td colSpan={8} className="px-5 py-8 text-center text-zinc-500">
                    Keine Treffer
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        )}
      </Card>
      <Pager offset={offset} total={total} onChange={setOffset} />
    </>
  );
}

function QuotaForm({
  orgId,
  quotaBytes,
  isDefault,
}: {
  orgId: string;
  quotaBytes: number;
  isDefault: boolean;
}) {
  const queryClient = useQueryClient();
  const [value, setValue] = useState(
    isDefault ? "" : String(Math.round((quotaBytes / 1024 ** 3) * 10) / 10),
  );
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await client.admin.orgs.setQuota({
        orgId,
        quotaGb: value.trim() ? Number(value.replace(",", ".")) : null,
      });
      toast.success("Kontingent gespeichert");
      await queryClient.invalidateQueries({ queryKey: orpc.admin.orgs.key() });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="p-5">
      <label htmlFor="org-quota" className="mb-1.5 block text-sm font-medium text-zinc-700">
        Speicher-Kontingent in GB
      </label>
      <div className="flex gap-2">
        <Input
          id="org-quota"
          inputMode="decimal"
          className="w-40"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder="Standard"
        />
        <Button type="submit" loading={busy}>
          Speichern
        </Button>
      </div>
      <p className="mt-1.5 text-xs text-zinc-500">
        Leer = Speicher des Tarifs (beim Tarif Intern: DEFAULT_ORG_QUOTA_GB)
      </p>
    </form>
  );
}

const limitText = (used: number, limit: number | null, unit = "") =>
  `${used}${unit} von ${limit === null ? "unbegrenzt" : `${limit}${unit}`}`;

function PlanCard({ org }: { org: AdminOrgDetail }) {
  const queryClient = useQueryClient();
  const [plan, setPlan] = useState<PlanId>(org.plan);
  const [busy, setBusy] = useState(false);
  useEffect(() => setPlan(org.plan), [org.plan]);
  const limits = PLANS[org.plan].limits;

  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    try {
      await client.admin.orgs.setPlan({ orgId: org.id, plan });
      toast.success(`Tarif ${PLANS[plan].name} gespeichert`);
      await queryClient.invalidateQueries({ queryKey: orpc.admin.orgs.key() });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusy(false);
    }
  }

  const rows: [string, string][] = [
    ["Mitglieder", limitText(org.memberCount, limits.members)],
    [
      "Untertitel diesen Monat",
      limitText(captionMinutesUsed(org.usage.captionSeconds), limits.captionMinutes, " Min."),
    ],
    ["Freistellungen diesen Monat", limitText(org.usage.cutouts, limits.cutouts)],
    ["Brand Kits", limitText(org.usage.brandKits, limits.brandKits)],
  ];
  return (
    <Card>
      <CardHeader
        title="Tarif"
        description={`${PLANS[org.plan].name} · ${formatPlanPrice(PLANS[org.plan])} im Monat`}
      />
      <ul className="divide-y divide-zinc-100">
        {rows.map(([label, value]) => (
          <li key={label} className="flex justify-between px-5 py-2 text-sm">
            <span>{label}</span>
            <span className="text-zinc-600 tabular-nums">{value}</span>
          </li>
        ))}
      </ul>
      <form onSubmit={submit} className="border-t border-zinc-100 p-5">
        <label htmlFor="org-plan" className="mb-1.5 block text-sm font-medium text-zinc-700">
          Tarif ändern
        </label>
        <div className="flex gap-2">
          <Select
            id="org-plan"
            className="w-48"
            value={plan}
            onChange={(e) => setPlan(e.target.value as PlanId)}
          >
            {PLAN_IDS.map((id) => (
              <option key={id} value={id}>
                {PLANS[id].name} ({formatPlanPrice(PLANS[id])})
              </option>
            ))}
          </Select>
          <Button type="submit" loading={busy} disabled={plan === org.plan}>
            Speichern
          </Button>
        </div>
        <p className="mt-1.5 text-xs text-zinc-500">
          Gilt sofort. Liegt die Organisation danach über einem Limit, bleibt alles erhalten, nur
          Neues wird abgelehnt.
        </p>
      </form>
    </Card>
  );
}

function RoleSelect({
  value,
  onChange,
  id,
  className = "w-36",
}: {
  value: OrgRole;
  onChange: (role: OrgRole) => void;
  id?: string;
  className?: string;
}) {
  return (
    <Select
      id={id}
      value={value}
      onChange={(e) => onChange(e.target.value as OrgRole)}
      className={className}
    >
      {[...ORG_ROLES].reverse().map((role) => (
        <option key={role} value={role}>
          {ROLE_LABELS[role]}
        </option>
      ))}
    </Select>
  );
}

/** Admins put existing accounts into an organization directly (no invitation mail). */
function AddMemberForm({ org }: { org: AdminOrgDetail }) {
  const queryClient = useQueryClient();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<OrgRole>("editor");
  const [busyId, setBusyId] = useState<string | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setSearch(query.trim()), 250);
    return () => clearTimeout(timer);
  }, [query]);

  const users = useQuery({
    ...orpc.admin.users.list.queryOptions({
      input: { query: search, filter: "all", limit: 8, offset: 0 },
    }),
    enabled: search.length >= 2,
    placeholderData: keepPreviousData,
  });
  const memberIds = new Set(org.members.map((m) => m.userId));

  async function add(userId: string, email: string) {
    setBusyId(userId);
    try {
      await client.admin.orgs.addMember({ orgId: org.id, userId, role });
      toast.success(`${email} ist jetzt ${ROLE_LABELS[role]} in „${org.name}“`);
      await queryClient.invalidateQueries({ queryKey: orpc.admin.key() });
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="border-t border-zinc-100 p-5">
      <p className="mb-3 text-sm font-medium text-zinc-700">Konto hinzufügen</p>
      <div className="flex flex-wrap items-end gap-3">
        <div className="relative min-w-56 flex-1">
          <Search className="absolute top-2.5 left-3 size-4 text-zinc-400" />
          <Input
            className="pl-9"
            placeholder="Name oder E-Mail suchen"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Konto suchen"
          />
        </div>
        <Field label="Rolle">
          <RoleSelect value={role} onChange={setRole} className="w-36" />
        </Field>
      </div>
      {search.length >= 2 ? (
        <ul className="mt-3 divide-y divide-zinc-100 rounded-lg border border-zinc-100">
          {users.data?.items.map((user) => {
            const isMember = memberIds.has(user.id);
            return (
              <li key={user.id} className="flex items-center gap-3 px-3 py-2 text-sm">
                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium">{user.name}</p>
                  <p className="truncate text-xs text-zinc-500">
                    {user.email}
                    {user.banned ? " · gesperrt" : ""}
                  </p>
                </div>
                {isMember ? (
                  <span className="text-xs text-zinc-500">Bereits Mitglied</span>
                ) : (
                  <Button
                    size="sm"
                    variant="secondary"
                    loading={busyId === user.id}
                    disabled={busyId !== null && busyId !== user.id}
                    onClick={() => add(user.id, user.email)}
                  >
                    <UserPlus /> Hinzufügen
                  </Button>
                )}
              </li>
            );
          })}
          {users.data?.items.length === 0 ? (
            <li className="px-3 py-4 text-center text-sm text-zinc-500">
              Kein Konto gefunden. Die Person muss sich zuerst selbst registrieren.
            </li>
          ) : null}
          {users.data && users.data.total > users.data.items.length ? (
            <li className="px-3 py-2 text-xs text-zinc-500">
              {users.data.total - users.data.items.length} weitere Treffer – bitte genauer suchen.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

function MembersCard({ org }: { org: AdminOrgDetail }) {
  const queryClient = useQueryClient();
  const [removing, setRemoving] = useState<AdminOrgDetail["members"][number] | null>(null);

  async function changeRole(userId: string, role: OrgRole) {
    try {
      await client.admin.orgs.setMemberRole({ orgId: org.id, userId, role });
      toast.success("Rolle geändert");
    } catch (error) {
      toast.error(errorMessage(error));
    } finally {
      await queryClient.invalidateQueries({ queryKey: orpc.admin.orgs.key() });
    }
  }

  return (
    <Card>
      <CardHeader
        title="Mitglieder"
        description="Owner verwalten alles; Admins zusätzlich Mitglieder; Editoren erstellen Inhalte; Betrachter sehen nur."
      />
      <ul className="divide-y divide-zinc-100">
        {org.members.map((m) => {
          const role = effectiveRole(m.role);
          return (
            <li key={m.userId} className="flex items-center gap-3 px-5 py-2.5 text-sm">
              <Link
                to="/admin/users/$userId"
                params={{ userId: m.userId }}
                className="min-w-0 flex-1 hover:text-brand-700"
              >
                <span className="block truncate font-medium">{m.name}</span>
                <span className="block truncate text-xs text-zinc-500">{m.email}</span>
              </Link>
              {role ? (
                <RoleSelect value={role} onChange={(next) => changeRole(m.userId, next)} />
              ) : (
                <Badge>{m.role}</Badge>
              )}
              <Button
                variant="ghost"
                size="icon"
                aria-label={`${m.name} entfernen`}
                onClick={() => setRemoving(m)}
              >
                <UserMinus />
              </Button>
            </li>
          );
        })}
        {org.members.length === 0 ? (
          <li className="px-5 py-6 text-center text-sm text-zinc-500">Keine Mitglieder</li>
        ) : null}
      </ul>
      <AddMemberForm org={org} />
      <ConfirmDialog
        open={removing !== null}
        onOpenChange={(open) => !open && setRemoving(null)}
        title="Aus der Organisation entfernen?"
        description={
          removing
            ? `${removing.name} (${removing.email}) verliert sofort den Zugriff auf alle Projekte und Dateien von „${org.name}“. Die Inhalte bleiben erhalten.`
            : undefined
        }
        confirmLabel="Entfernen"
        danger
        onConfirm={async () => {
          if (!removing) return;
          try {
            await client.admin.orgs.removeMember({ orgId: org.id, userId: removing.userId });
            toast.success("Mitglied entfernt");
            await queryClient.invalidateQueries({ queryKey: orpc.admin.key() });
          } catch (error) {
            toast.error(errorMessage(error));
          }
        }}
      />
    </Card>
  );
}

export function AdminOrgPage() {
  const { orgId } = useParams({ strict: false }) as { orgId: string };
  const org = useQuery(orpc.admin.orgs.get.queryOptions({ input: { orgId } }));
  if (!org.data) {
    return org.error ? (
      <p className="text-red-600">{errorMessage(org.error)}</p>
    ) : (
      <Skeleton className="h-96" />
    );
  }
  const o = org.data;
  const percent = usedPercent(o.storageBytes, o.quotaBytes);
  return (
    <>
      <Link
        to="/admin/orgs"
        className="mb-3 inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900"
      >
        <ArrowLeft className="size-4" /> Alle Organisationen
      </Link>
      <AdminPageHeader
        title={o.name}
        description={`${o.slug} · angelegt ${formatDate(o.createdAt)} · zuletzt bearbeitet ${o.lastActivityAt ? formatDateTime(o.lastActivityAt) : "–"}`}
      />
      <div className="mb-6 grid gap-3 sm:grid-cols-4">
        {[
          ["Mitglieder", o.memberCount],
          ["Projekte", o.projectCount],
          ["Dateien", o.documentCount],
          ["Medien", o.assetCount],
        ].map(([label, value]) => (
          <Card key={label} className="p-4">
            <p className="text-xs font-medium text-zinc-700">{label}</p>
            <p className="mt-1 text-2xl font-semibold tabular-nums">{value}</p>
          </Card>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-2">
        <MembersCard org={o} />
        <Card>
          <CardHeader
            title="Speicher"
            description={`${formatBytes(o.storageBytes)} von ${formatBytes(o.quotaBytes)}${o.quotaIsDefault ? " (Tarif)" : " (eigenes Kontingent)"}`}
          />
          <div className="px-5 pt-4">
            <Progress value={percent} tone={tone(percent)} />
          </div>
          <ul className="divide-y divide-zinc-100 px-0 pt-2">
            {o.storageByCategory.map((c) => (
              <li key={c.category} className="flex justify-between px-5 py-2 text-sm">
                <span>
                  {STORAGE_CATEGORY_LABELS[c.category as keyof typeof STORAGE_CATEGORY_LABELS] ??
                    c.category}
                </span>
                <span className="text-zinc-600 tabular-nums">{formatBytes(c.bytes)}</span>
              </li>
            ))}
          </ul>
          <QuotaForm orgId={orgId} quotaBytes={o.quotaBytes} isDefault={o.quotaIsDefault} />
        </Card>
        <PlanCard org={o} />
      </div>
    </>
  );
}
